import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { TagIcon, ExternalLinkIcon, ChevronRightIcon } from 'lucide-react';
import { PortfolioModal } from '../components/portfolio/PortfolioModal';
export function Portfolio() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const projects: Project[] = [{
    id: 1,
    title: 'Debt Rescue',
    category: 'Financial Services / Business Solutions',
    quote: '"Professional platform handling sensitive financial matters with complete discretion"',
    client: 'Debt Rescue Team',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1c8vq2ajwLeXwihGnzEfYF/image.png',
    challenge: 'Debt Rescue needed a professional, trustworthy platform to help distressed Ltd companies find buyers willing to take over their debts and liabilities. The website had to convey security, confidentiality, and professionalism while making it easy for business owners to reach out during a stressful time.',
    solution: 'We created a clean, professional website with clear messaging and a strong emphasis on confidentiality and trust. The site features prominent WhatsApp and email contact options, GDPR-compliant secure processes, and reassuring statistics to build confidence. We integrated professional email, Mailchimp for newsletter management, Google Analytics for tracking, and premium hosting for reliability.',
    results: ['Successfully transferred 150+ companies', 'Average 24-hour response time maintained', '100% confidential service with GDPR compliance', 'Secure, professional platform for sensitive financial matters'],
    techStack: ['Custom Design', 'Professional Email Setup', 'Mailchimp Newsletter Integration', 'Google Analytics', 'Premium Hosting', 'GDPR Compliance Features', 'WhatsApp Business Integration'],
    link: 'https://debtrescue.co.uk'
  }, {
    id: 2,
    title: 'ooOhMy.co.uk',
    category: 'E-commerce / Fashion',
    quote: '"Increased our online sales by 45% in the first month"',
    client: 'Sarah Johnson, Owner',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pQKeoA3T9GzSKJQNGTdgoz/websites_1_-_ooOhmy.co.uk.jpg',
    challenge: 'ooOhMy needed a modern e-commerce site that could handle their growing product inventory while providing a seamless shopping experience for customers. Their previous site was slow, difficult to navigate, and not optimized for mobile devices.',
    solution: 'We developed a fully responsive e-commerce platform with advanced product filtering, wishlist functionality, and seamless checkout process. The site was built with performance in mind, ensuring fast loading times even on mobile connections.',
    results: ['45% increase in online sales within the first month', '60% reduction in cart abandonment rate', '35% improvement in average page load speed', 'Seamless inventory management integration'],
    techStack: ['Shopify Design', 'Custom Theme', 'Shopify Payment Gateway', 'Klaviyo Newsletter Integration', 'Royal Mail Integration', 'Several other add-ons'],
    link: 'https://ooohmy.co.uk'
  }, {
    id: 3,
    title: 'Northampton Driving School',
    category: 'Education / Services',
    quote: '"Bookings up 60% since the new website launch"',
    client: 'David Thompson, Director',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d5GzTyf2oC8ykFDj3X9wmi/Website_3_-_northamptondrivingschool.co.uk.jpg',
    challenge: 'Northampton Driving School needed a website that would appeal to younger students while providing easy online booking functionality. Their old site was outdated and required manual booking management.',
    solution: "We created a modern, engaging website with an integrated booking system that syncs with instructors' calendars. The site includes a student portal for lesson notes and progress tracking, as well as informative content about learning to drive.",
    results: ['60% increase in bookings after launch', 'Reduced administrative workload by 75%', 'Improved student engagement and retention', 'First page Google ranking for key local search terms'],
    techStack: ['Total Drive Integration app', 'Stripe payments', 'Responsive design', 'Local SEO targeting'],
    link: 'https://northamptondrivingschool.co.uk'
  }, {
    id: 4,
    title: 'Medicolegal Partners',
    category: 'Healthcare / Legal',
    quote: '"Professional site that perfectly represents our brand"',
    client: 'Dr. Jennifer Adams, Director',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4kYo3C4gd9QjfCrgk7a12h/Website_2_-_medicolegal-psychiatry.co.uk.jpg',
    challenge: 'Medicolegal Partners required a professional website that would inspire trust and clearly communicate their specialized psychiatric services for legal cases. They needed to balance approachability with clinical expertise.',
    solution: "We designed a sophisticated yet accessible website with clear information architecture, detailed service descriptions, and a secure referral system for legal professionals. The site emphasizes the team's credentials while maintaining a compassionate tone.",
    results: ['Significant increase in qualified referrals', 'Reduced time spent explaining services to potential clients', 'Improved perception of practice professionalism', 'Secure document sharing functionality for sensitive cases'],
    techStack: ['Custom Design', 'WordPress', 'Secure Document Portal', 'GDPR Compliance Features'],
    link: 'https://medicolegal-psychiatry.co.uk'
  }, {
    id: 5,
    title: 'SureFix.it',
    category: 'Home Services / Repair',
    quote: '"Lead generation increased 75% year on year"',
    client: 'Robert Lewis, Founder',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2mYs5H4cmXngmP57UGoyEE/Website_4_-_surefix.it.com.jpg',
    challenge: 'SureFix.it needed a website that would generate leads for their appliance repair business and clearly communicate their service areas and expertise. Their previous website had poor conversion rates and unclear service information.',
    solution: 'We built a conversion-focused website with prominent call-to-actions, service area maps, and detailed information about the appliances they repair. The site includes an online booking system and emergency contact options.',
    results: ['75% increase in lead generation year-on-year', 'Improved customer understanding of service offerings', 'Reduced phone time explaining basic service information', 'Higher quality leads with appropriate service expectations'],
    techStack: ['WordPress', 'Booking System', 'Google Maps Integration', 'Schema Markup for Local SEO'],
    link: 'https://surefix.it.com'
  }, {
    id: 6,
    title: 'AxisCare.it',
    category: 'Home Services / Repair',
    quote: '"ROI on website in just 2 months. Incredible!"',
    client: 'Michael Chen, CEO',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wqw4YWP28GPXqhAPXrrgAj/Website_5_-_axiscare.it.com.jpg',
    challenge: 'AxisCare.it was a new appliance repair business needing to establish online presence and generate leads quickly. They needed a website that would help them compete with established repair services in their area.',
    solution: 'We created a highly optimized website focused on local SEO, with targeted content for each service area. The site features emergency repair options, detailed service descriptions, and trust-building elements like guarantees and testimonials.',
    results: ['ROI achieved within just 2 months of launch', 'First page Google rankings for key local search terms', 'Established brand credibility in a competitive market', 'Effective lead qualification system reducing wasted callouts'],
    techStack: ['WordPress', 'Advanced SEO Optimization', 'Local Business Schema', 'Mobile-First Design'],
    link: 'https://axiscare.it.com'
  }];
  const openModal = (project: Project) => {
    setSelectedProject(project);
  };
  const closeModal = () => {
    setSelectedProject(null);
  };
  return <>
      <Helmet data-id="element-1554">
        <title data-id="element-1555">Web Design Portfolio | Real UK Client Projects</title>
        <meta name="description" content="Explore our latest websites across e-commerce, education, medical and trades. See results, visuals and client comments." data-id="element-1556" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-4 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1557">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1558">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1559">
            <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1560">
              Our Work
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1561">
              Portfolio of Real Client Projects
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8" data-id="element-1562">
              Explore our latest work across various industries. Each project
              represents our commitment to creating beautiful, functional
              websites that deliver measurable results for our clients.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-4 md:py-6 bg-white dark:bg-[#1E1E1E]" data-id="element-1563">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1564">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1565">
            {projects.map((project, index) => <motion.div key={project.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white dark:bg-[#1E1E1E] rounded-xl overflow-hidden shadow-md border border-gray-100 dark:border-gray-800 hover:shadow-xl transition-shadow duration-300" onClick={() => openModal(project)} data-id="element-1566">
                <div className="relative overflow-hidden h-56" data-id="element-1567">
                  <img src={project.image} alt={`ALT: ${project.title} website homepage — Bright Pixxel Studios case study`} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" data-id="element-1568" />
                  <div className="absolute top-4 left-4 bg-white dark:bg-[#1E1E1E] py-1 px-3 rounded-full shadow-sm" data-id="element-1569">
                    <div className="flex items-center space-x-1" data-id="element-1570">
                      <TagIcon size={14} className="text-[#3AA6F9]" data-id="element-1571" />
                      <span className="text-xs font-medium" data-id="element-1572">
                        {project.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6" data-id="element-1573">
                  <h3 className="text-xl font-semibold mb-2" data-id="element-1574">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm italic mb-4" data-id="element-1575">
                    {project.quote}
                  </p>
                  <div className="flex justify-between items-center" data-id="element-1576">
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="text-[#3AA6F9] text-sm hover:underline flex items-center" onClick={e => e.stopPropagation()} data-id="element-1577">
                      <span data-id="element-1578">Visit site</span>
                      <ExternalLinkIcon size={14} className="ml-1" data-id="element-1579" />
                    </a>
                    <button className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center hover:text-[#3AA6F9] dark:hover:text-[#3AA6F9] transition-colors" data-id="element-1580">
                      <span data-id="element-1581">View case study</span>
                      <ChevronRightIcon size={16} className="ml-1" data-id="element-1582" />
                    </button>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-4 md:py-6 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1583">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1584">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1585">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1586">
              Ready to join our success stories?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-1587">
              Let's discuss your project and how we can help you achieve your
              business goals with a website that truly works for you.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1588">
              <a href="/contact" className="bg-[#3AA6F9] text-white hover:bg-[#2A95E8] px-6 py-3 rounded-md font-medium transition-colors" data-id="element-1589">
                Request a Quote
              </a>
              <a href="/pricing" className="border-2 border-[#3AA6F9] text-[#3AA6F9] hover:bg-[#3AA6F9] hover:text-white px-6 py-3 rounded-md font-medium transition-colors" data-id="element-1590">
                View Pricing
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portfolio Modal */}
      {selectedProject && <PortfolioModal project={selectedProject} onClose={closeModal} data-id="element-1591" />}
    </>;
}
// TypeScript interface for project data
interface Project {
  id: number;
  title: string;
  category: string;
  quote: string;
  client: string;
  image: string;
  challenge: string;
  solution: string;
  results: string[];
  techStack: string[];
  link: string;
}